/**
 * api/client.ts
 * Central Axios-style fetch wrapper.
 * Every method maps 1-to-1 with a backend REST endpoint.
 * Replace BASE_URL with your deployed API when backend is live.
 *
 * Backend expected: Node.js + Express + PostgreSQL
 * Auth: Bearer JWT in Authorization header
 */

import type {
  AuthResponse, LoginRequest, SignupRequest,
  Note, CreateNoteRequest, UpdateNoteRequest,
  Folder, CreateFolderRequest, UpdateFolderRequest,
  SearchResponse, SearchRequest,
  User,
} from '../types';

// ── Config ────────────────────────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// ── Token helpers ─────────────────────────────────────────────────────────────
export const getToken  = (): string | null => localStorage.getItem('Jötr_token');
export const setToken  = (t: string)       => localStorage.setItem('Jötr_token', t);
export const clearAuth = ()                => { localStorage.removeItem('Jötr_token'); localStorage.removeItem('Jötr_user'); };

// ── Base fetch ────────────────────────────────────────────────────────────────
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Network error' }));
    const apiErr = new Error(err.message || 'Something went wrong') as Error & { statusCode: number; code: string };
    apiErr.statusCode = res.status; apiErr.code = err.code || 'UNKNOWN'; throw apiErr;
  }

  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// ── Auth API  →  POST /api/auth/* ──────────────────────────────────────────
export const authApi = {
  login:  (body: LoginRequest)  => request<AuthResponse>('/auth/login',  { method:'POST', body: JSON.stringify(body) }),
  signup: (body: SignupRequest) => request<AuthResponse>('/auth/signup', { method:'POST', body: JSON.stringify(body) }),
  logout: ()                    => request<void>('/auth/logout', { method:'POST' }),
  me:     ()                    => request<User>('/auth/me'),
  googleCallback: (code: string) => request<AuthResponse>('/auth/google', { method:'POST', body: JSON.stringify({ code }) }),
  forgotPassword:       (email: string)           => request<void>('/auth/forgot-password',       { method:'POST', body: JSON.stringify({ email }) }),
  resetPassword:        (token: string, p: string)  => request<void>('/auth/reset-password',        { method:'POST', body: JSON.stringify({ token, password: p }) }),
  resendVerification:   ()                          => request<void>('/auth/resend-verification',   { method:'POST' }),
  googleSignIn:         (idToken: string)           => request<AuthResponse>('/auth/google',         { method:'POST', body: JSON.stringify({ idToken }) }),
};

// ── Notes API  →  /api/notes ──────────────────────────────────────────────
export const notesApi = {
  list: (params?: { folderId?: string; tag?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams(params as Record<string,string>).toString();
    return request<Note[]>(`/notes${q ? '?' + q : ''}`);
  },
  get:    (id: string)                        => request<Note>(`/notes/${id}`),
  create: (body: CreateNoteRequest)           => request<Note>('/notes',       { method:'POST',   body: JSON.stringify(body) }),
  update: (id: string, body: UpdateNoteRequest) => request<Note>(`/notes/${id}`, { method:'PUT',    body: JSON.stringify(body) }),
  delete: (id: string)                        => request<void>(`/notes/${id}`, { method:'DELETE' }),
  pin:    (id: string, isPinned: boolean)     => request<Note>(`/notes/${id}/pin`, { method:'PUT',   body: JSON.stringify({ isPinned }) }),
  share:  (id: string)                          => request<{ shareUrl: string; token: string }>(`/notes/${id}/share`, { method:'POST' }),
  unshare:(id: string)                          => request<void>(`/notes/${id}/share`, { method:'DELETE' }),
  getPublic:(token: string)                     => request<any>(`/notes/public/${token}`),
};

// ── Folders API  →  /api/folders ─────────────────────────────────────────
export const foldersApi = {
  list:   ()                                      => request<Folder[]>('/folders'),
  get:    (id: string)                            => request<Folder>(`/folders/${id}`),
  create: (body: CreateFolderRequest)             => request<Folder>('/folders',       { method:'POST',   body: JSON.stringify(body) }),
  update: (id: string, body: UpdateFolderRequest) => request<Folder>(`/folders/${id}`, { method:'PATCH',  body: JSON.stringify(body) }),
  delete: (id: string)                            => request<void>(`/folders/${id}`,   { method:'DELETE' }),
};

// ── Search API  →  /api/search ───────────────────────────────────────────
export const searchApi = {
  query: (body: SearchRequest) => request<SearchResponse>('/search', { method:'POST', body: JSON.stringify(body) }),
};

// ── User API  →  /api/user ────────────────────────────────────────────────
export const userApi = {
  update: (body: Partial<Pick<User,'name'>>) => request<User>('/user', { method:'PATCH', body: JSON.stringify(body) }),
  changePassword: (current: string, next: string) => request<void>('/user/password', { method:'PUT', body: JSON.stringify({ currentPassword: current, newPassword: next }) }),
  deleteAccount: () => request<void>('/user', { method:'DELETE' }),
};